/**
 * PtyHarness — drives the `claude` CLI under a pseudo-terminal behind the
 * generic `AgentHarness` interface.  Selected at runtime via
 * `createHarness("pty")`. It is the UNCONDITIONAL harness for the task chat
 * (cards); see `resolveHarnessKind()` in `runner/query-bridge.ts`. The only way
 * to put a card on the SDK harness is the maintainer override
 * `CONVEYOR_FORCE_SDK_CARDS=1`.
 *
 * Keep-alive: the interactive `claude` process outlives any single query. When
 * a query ends via a clean transcript `result`, the harness PARKS the
 * `PtySession` (process still running) instead of killing it, so the
 * Connected-TUI stays live and interactive while the agent is idle. The next
 * query reuses that process when its resume target + spawn options match;
 * otherwise it tears the parked one down and spawns fresh. Only a real process
 * death (crash while parked, or dispose() on shutdown/hard-stop) signals
 * `pty:ended` so the server clears the ring and the tab hides.
 */

import type {
  AgentHarness,
  HarnessEvent,
  HarnessQueryOptions,
  HarnessToolDefinition,
  HarnessMcpServer,
  HarnessUserMessage,
  PtyBridge,
} from "../types.js";
import { PtySession } from "./session.js";
import { PtyMcpServer } from "./mcp-server.js";
import { ClaudeTuiAdapter } from "./adapters/claude.js";
import type { TuiAdapter } from "./adapters/types.js";
import { ensureUsableClaudeConfigHome } from "./config-home-health.js";
import { createServiceLogger } from "../../utils/logger.js";

export { PtySession, PtyMcpServer };

// Grace window before a process death is reported as `pty:ended`. A respawn
// (the common mid-turn abort → respawn-with-resume flow, e.g. a superseding
// chat message) within this window cancels the signal, so the Connected-TUI tab
// doesn't flap. Parked-death and dispose() bypass the grace and signal at once.
const ENDED_GRACE_MS = 4000;

export class PtyHarness implements AgentHarness {
  private static readonly log = createServiceLogger("pty-harness");

  /**
   * `bridge` relays raw terminal I/O to/from the S2 server (and on to the S5
   * terminal). It is undefined for SDK-only callers and PTY runs that never
   * attach a relay; the session simply discards stdout in that case.
   */
  constructor(
    private readonly bridge?: PtyBridge,
    private readonly adapter: TuiAdapter = new ClaudeTuiAdapter(),
  ) {}

  /** Fingerprint of the spawn-time options a reused process cannot change. */
  private fingerprintOf(options: HarnessQueryOptions): string {
    return this.adapter.spawnFingerprint({
      model: options.model,
      permissionMode: options.permissionMode,
      ...(options.appendSystemPrompt ? { appendSystemPrompt: options.appendSystemPrompt } : {}),
      cwd: options.cwd,
    });
  }

  /** The session currently streaming events, if any — repaint target. */
  private activeSession: PtySession | null = null;
  /** A completed-but-alive session kept for the next turn (keep-alive). */
  private parked: PtySession | null = null;
  /** Pending "process died" → `pty:ended` timer (grace window). */
  private endedTimer: ReturnType<typeof setTimeout> | null = null;
  /** Passive-activity subscriber, re-attached to whichever session is parked. */
  private passiveHandler: (() => void) | null = null;

  /**
   * Wiggle the live pty's size so the CLI repaints its whole screen. No-op on
   * the SDK harness. Falls back to the parked session so an API reconnect while
   * the CLI is idle still re-seeds the server's scrollback ring.
   */
  forceRepaint(): void {
    (this.activeSession ?? this.parked)?.forceRepaint();
  }

  async *executeQuery(opts: {
    prompt: string | AsyncGenerator<HarnessUserMessage, void, unknown>;
    options: HarnessQueryOptions;
    resume?: string;
  }): AsyncGenerator<HarnessEvent, void> {
    const want = opts.resume ?? opts.options.resume;
    const fingerprint = this.fingerprintOf(opts.options);

    let session: PtySession;
    if (this.parked?.canReuse(want, fingerprint)) {
      // Reuse the live process: feed the follow-up prompt into the existing pty
      // instead of respawning. A new turn means no death is imminent — cancel
      // any pending ended signal.
      session = this.parked;
      this.parked = null;
      this.cancelEndedTimer();
      await session.beginTurn(opts.prompt, opts.options);
    } else {
      // Lineage/options mismatch, no parked session, or a dead one: tear down
      // any parked process (respawn — suppress its ended signal, a fresh spawn
      // follows immediately) and start a new one.
      if (this.parked) {
        const stale = this.parked;
        this.parked = null;
        this.cancelEndedTimer();
        await stale.teardown();
      }
      session = new PtySession(opts.prompt, opts.options, want, this.bridge, this.adapter);
      // Before touching the config home (credentials, transcript dir), make sure
      // the shared ~/.claude GCS FUSE mount is writable. A dropped mount surfaces
      // as ENOTCONN on the very next mkdir and wedges the session with no in-place
      // recovery; this self-heals to a pod-local config home instead of dying and
      // re-failing on every wake. Claude-only: opencode doesn't use ~/.claude.
      if (this.adapter.capabilities.structuredEvents) {
        await ensureUsableClaudeConfigHome(opts.options.cwd, PtyHarness.log);
      }
      // The interactive TUI ignores CLAUDE_CODE_OAUTH_TOKEN (headless-only); in
      // Conveyor cloud envs, materialize it as `<configHome>/.credentials.json`
      // so the CLI starts authenticated instead of at the login-method picker,
      // and complete the onboarding flags + workspace trust in .claude.json so
      // neither an abandoned first-run wizard nor the folder-trust dialog can
      // park the TUI. Spawn-only — a reused process is already authenticated.
      await this.adapter.prepareEnvironment({ cwd: opts.options.cwd });
      // Register the parked-death watch once for this process's lifetime.
      session.onExit(() => this.handleSessionExit(session));
      await session.start();
    }

    yield* this.drain(session);
  }

  /**
   * PTY-only: subscribe to "the parked CLI produced transcript activity with no
   * query running" (a human typed into the idle Connected-TUI). Attaches to the
   * currently-parked session and to any session parked later.
   */
  onPassiveActivity(handler: () => void): () => void {
    this.passiveHandler = handler;
    const unsubParked = this.parked?.onPassiveActivity(handler);
    return () => {
      if (this.passiveHandler === handler) this.passiveHandler = null;
      unsubParked?.();
    };
  }

  /**
   * PTY-only: drain a passive turn against the parked process — the human
   * already submitted in the TUI, so no prompt is fed. Empty stream when nothing
   * is parked.
   */
  async *executePassiveTurn(options: HarnessQueryOptions): AsyncGenerator<HarnessEvent, void> {
    const session = this.parked;
    if (!session) return;
    this.parked = null;
    this.cancelEndedTimer();
    session.beginPassiveTurn(options);
    yield* this.drain(session);
  }

  /**
   * Shared query/passive-turn drain: stream the session's events, then either
   * park it (clean result → keep the process for the next turn) or tear it down
   * (abort/error/early-return → real death, schedule the ended signal).
   */
  private async *drain(session: PtySession): AsyncGenerator<HarnessEvent, void> {
    this.activeSession = session;
    let clean = false;
    try {
      for await (const event of session.events()) {
        yield event;
      }
      // The stream completed. Parkable only if it ended via a clean transcript
      // result and the process is still alive.
      clean = session.endedCleanly && !session.isToreDown && !session.hasExited;
    } finally {
      if (this.activeSession === session) this.activeSession = null;
      if (clean) {
        this.park(session);
      } else {
        await session.teardown();
        // Consumer abandoned / aborted / errored → the process is (being)
        // killed. Report ended after the grace window unless a respawn beats it.
        this.scheduleEnded();
      }
    }
  }

  /** Park a live, idle session for reuse and (re)wire the passive listener. */
  private park(session: PtySession): void {
    this.parked = session;
    if (this.passiveHandler) session.onPassiveActivity(this.passiveHandler);
  }

  /**
   * Parked/active process exited on its own (crash). A mid-turn death is
   * handled by drain()'s finally; here we only act on a death while PARKED —
   * tear down the husk and report ended immediately (no respawn is coming).
   */
  private handleSessionExit(session: PtySession): void {
    if (this.parked !== session) return;
    this.parked = null;
    void session.teardown();
    this.notifyEnded();
  }

  private scheduleEnded(): void {
    if (this.endedTimer) return;
    this.endedTimer = setTimeout(() => {
      this.endedTimer = null;
      this.notifyEnded();
    }, ENDED_GRACE_MS);
  }

  private cancelEndedTimer(): void {
    if (this.endedTimer) {
      clearTimeout(this.endedTimer);
      this.endedTimer = null;
    }
  }

  private notifyEnded(): void {
    this.cancelEndedTimer();
    this.bridge?.sendEnded?.();
  }

  /**
   * Tear down every live process (parked + active) and signal ended. Called by
   * the runner on stop/shutdown so the parked CLI never outlives the session and
   * the Connected-TUI tab hides promptly.
   */
  async dispose(): Promise<void> {
    this.cancelEndedTimer();
    const toKill = [this.parked, this.activeSession].filter((s): s is PtySession => s !== null);
    this.parked = null;
    this.activeSession = null;
    for (const session of toKill) {
      try {
        await session.teardown();
      } catch {
        /* already torn down */
      }
    }
    if (toKill.length > 0) this.notifyEnded();
  }

  createMcpServer(config: { name: string; tools: HarnessToolDefinition[] }): HarnessMcpServer {
    return new PtyMcpServer(config.name, config.tools);
  }
}
